import { memo, useMemo } from 'react';

import { StructuredAnswer, StructuredSurveyTemplate } from '@dreryk/egabinet-survey-frontend-api';

import { Button, ButtonVariant, StrongText, StyledFlex } from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../../../translations';
import { useMedicalProfileFormContext } from '../../MedicalProfileFormContext';
import { SectionNodeViewModel } from '../../tab/MedicalProfileTab.types';
import { collectNodeControlQuestionIds } from '../../tab/MedicalProfileTab.utils';
import { useVisibilityResolver } from '../SectionRow.hooks';
import { SectionNodeFields } from '../SectionNodeFields';

type EntryListItemProps = {
   readonly templateCode: string;
   readonly template: StructuredSurveyTemplate;
   readonly initialAnswers: StructuredAnswer[];
   readonly node: SectionNodeViewModel;
   readonly entryKey: string;
   readonly position: number;
   readonly onRemove: (entryKey: string) => void;
};

export const EntryListItem = memo(
   (props: EntryListItemProps): JSX.Element => {
      const { register, readonly } = useMedicalProfileFormContext();
      const controlQuestionIds = useMemo(() => collectNodeControlQuestionIds(props.template, props.node, true), [
         props.template,
         props.node,
      ]);
      const entry = useMemo(() => ({ sectionId: props.node.sectionId, key: props.entryKey }), [
         props.node.sectionId,
         props.entryKey,
      ]);
      const entryNode = useMemo(() => ({ ...props.node, repeatable: false }), [props.node]);
      const entryNodes = useMemo(() => [entryNode], [entryNode]);
      const scope = useMemo(() => ({ entry: entry }), [entry]);
      const visibility = useVisibilityResolver({
         templateCode: props.templateCode,
         template: props.template,
         initialAnswers: props.initialAnswers,
         controlQuestionIds: controlQuestionIds,
         entry: entry,
      });

      const handleRemove = (): void => props.onRemove(props.entryKey);

      return (
         <StyledFlex direction={'column'} gap={16}>
            <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
               <StrongText testId={`entryTitle-${props.node.sectionId}-${props.entryKey}`}>
                  {`${props.node.title ?? ''} ${props.position}`.trim()}
               </StrongText>
               {readonly ? null : (
                  <Button
                     testId={`entryRemove-${props.node.sectionId}-${props.entryKey}`}
                     label={appointmentsTranslationKeys.medicalProfile.entry.remove}
                     type={'button'}
                     variant={ButtonVariant.SECONDARY}
                     onClick={handleRemove}
                  />
               )}
            </StyledFlex>
            <SectionNodeFields
               register={register}
               templateCode={props.templateCode}
               nodes={entryNodes}
               visibility={visibility}
               disabled={readonly}
               scope={scope}
            />
         </StyledFlex>
      );
   }
);
